import { RecordingSilenceAlert } from './RecordingSilenceAlert';
import { useRecordingState } from '@/contexts/RecordingStateContext';
import { useRecordingControls } from '@/contexts/RecordingControlsContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, MoveRight, Play, Square } from "lucide-react";
import { formatTime } from "@/utils/formatTime";
import { toast } from "sonner";

interface RootState {
  recording: {
    isRecording: boolean;
    recordingDuration: number;
    viewMode: "main" | "recording" | "after-record";
  };
}

interface FloatingRecordingWidgetProps {
  onStopRecording: () => void;
  onContinueRecording: () => void;
}

export const FloatingRecordingWidget: React.FC<FloatingRecordingWidgetProps> = ({
  onStopRecording,
  onContinueRecording,
}) => {
  const { isSilenceWarningVisible, isRecordingPaused } = useRecordingState();
  const { pauseRecordingRef, dismissSilenceWarningRef } = useRecordingControls();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRecording, recordingDuration, viewMode } = useSelector((state: RootState) => state.recording);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const hasShownBackgroundNoticeRef = useRef(false);

  // Only show the widget when recording or after recording (but not on recordings page)
  const isOnRecordingsPage = location.pathname === "/recordings";
  const shouldShow = (isRecording || viewMode === "after-record") && !isOnRecordingsPage;

  useEffect(() => {
    if (!isRecording) {
      hasShownBackgroundNoticeRef.current = false;
      return;
    }

    if (!isOnRecordingsPage && !hasShownBackgroundNoticeRef.current) {
      hasShownBackgroundNoticeRef.current = true;
      toast.info("ההקלטה ממשיכה ברקע. ניתן לשלוט בה מהחלונית הקבועה.", { duration: 6000 });
    }
  }, [isOnRecordingsPage, isRecording]);

  if (!shouldShow) return null;

  const handleNavigateToRecordings = () => {
    navigate("/recordings");
  };

  return (
    <div className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] start-4 z-50 max-w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-bottom-4">
      <div className="flex w-full max-w-sm flex-col gap-2">
        <RecordingSilenceAlert visible={isSilenceWarningVisible}
          onPause={pauseRecordingRef.current ? () => pauseRecordingRef.current?.() : undefined}
          onStop={onStopRecording} onDismiss={() => dismissSilenceWarningRef.current?.()} />
        {isRecording && pauseRecordingRef.current && <Button type="button" variant="outline" size="sm"
          onClick={() => pauseRecordingRef.current?.()}>
          {t(isRecordingPaused ? 'recordingSilence.resume' : 'recordingSilence.pause')}
        </Button>}
      <div className="flex items-center gap-3 rounded-xl border border-amber-400 bg-amber-50 p-3 shadow-lg shadow-amber-900/15">
        {/* Recording indicator */}
        {isRecording && (
          <div className="flex items-center gap-2">
            <div className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive" />
            </div>
            {!isCollapsed && <span className="text-sm font-medium text-chen-900">הקלטה פעילה</span>}
          </div>
        )}

        {viewMode === "after-record" && !isRecording && !isCollapsed && (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-primary" />
            <span className="text-sm font-medium text-chen-900">הקלטה הושלמה</span>
          </div>
        )}

        {/* Duration */}
        <div className="min-w-14 flex-1 text-center">
          <span className="text-lg font-semibold text-chen-900 tabular-nums">{formatTime(recordingDuration)}</span>
        </div>

        {/* Actions */}
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            {isRecording && (
              <Button
                onClick={onStopRecording}
                variant="destructive"
                size="sm"
                className="gap-2"
                aria-label="עצור הקלטה"
              >
                <Square className="h-4 w-4" />
                עצירה
              </Button>
            )}

            {viewMode === "after-record" && !isRecording && (
              <Button onClick={onContinueRecording} variant="default" size="sm" className="gap-2">
                <Play className="h-4 w-4" />
                המשך הקלטה
              </Button>
            )}

            <Button onClick={handleNavigateToRecordings} variant="outline" size="sm" className="gap-2" aria-label="עבור להקלטות">
              <MoveRight className="h-4 w-4" />
              לדף הקלטות
            </Button>
          </div>
        )}

        {isCollapsed && viewMode === "after-record" && !isRecording && (
          <Button
            onClick={onContinueRecording}
            variant="default"
            size="icon"
            aria-label="המשך הקלטה"
            title="המשך הקלטה"
          >
            <Play className="h-4 w-4" />
          </Button>
        )}

        <Button
          onClick={() => {
            setIsCollapsed((value) => !value);
          }}
          variant="ghost"
          size="icon"
          aria-label={isCollapsed ? "הרחב את חלונית ההקלטה" : "צמצם את חלונית ההקלטה"}
        >
          {isCollapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>

      </div>
      </div>
    </div>
  );
};
